import React, { useEffect, useState } from 'react';
import { Calendar, Clock, Mail, Menu, MessageSquare, PhoneCall, PhoneForwarded, PhoneOff, Plus, Settings, Trash2, Users, Volume2, X } from 'lucide-react';
import { prettyNumber } from '../CallDialog';

// Block settings panel, ported from the DialFlow prototype. Each block type has its own small component so that
// hooks are never called conditionally (the prototype called useState inside a switch).

const KEYS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '#'];
const DAYS = [['mon', 'Mon'], ['tue', 'Tue'], ['wed', 'Wed'], ['thu', 'Thu'], ['fri', 'Fri'], ['sat', 'Sat'], ['sun', 'Sun']];
const TIMEZONES = ['Asia/Kolkata', 'Asia/Dubai', 'Asia/Singapore', 'Europe/London', 'America/New_York', 'UTC'];
const ICONS = {
  start: [PhoneCall, 'var(--cyan-accent)'], playAudio: [Volume2, 'var(--violet-accent)'], ivrMenu: [Menu, 'var(--emerald-accent)'],
  officeHours: [Clock, 'var(--amber-accent)'], team: [Users, 'var(--blue-accent)'], voicemail: [Mail, 'var(--pink-accent)'],
  sendSMS: [MessageSquare, 'var(--indigo-accent)'], calendar: [Calendar, 'var(--purple-accent)'],
  callForward: [PhoneForwarded, 'var(--blue-accent)'], hangUp: [PhoneOff, 'var(--rose-accent)'],
};

const Field = ({ label, help, children }) => (
  <div className="form-group"><label>{label}</label>{children}{help && <span className="field-help">{help}</span>}</div>
);

function StartSettings({ data, set, numbers }) {
  return (
    <Field label="Incoming number" help="Calls to this number follow this flow. &quot;All company numbers&quot; covers every number without its own flow.">
      <select value={data.phoneNumber || ''} onChange={(e) => set('phoneNumber', e.target.value)}>
        <option value="">All company numbers</option>
        {numbers.map((n) => <option key={n.number} value={n.number}>{prettyNumber(n.number)}{n.label ? ` · ${n.label}` : ''}</option>)}
      </select>
    </Field>
  );
}

function PlayAudioSettings({ data, set }) {
  return (
    <>
      <Field label="Audio source mode">
        <div className="radio-toggle-group">
          <button type="button" className={data.sourceType !== 'url' ? 'active' : ''} onClick={() => set('sourceType', 'text')}>Text-To-Speech</button>
          <button type="button" className={data.sourceType === 'url' ? 'active' : ''} onClick={() => set('sourceType', 'url')}>Audio URL (MP3)</button>
        </div>
      </Field>
      {data.sourceType === 'url' ? (
        <Field label="Audio file URL" help="An https:// link to an .mp3 or .wav file. Uploading files is coming soon.">
          <input type="text" placeholder="https://example.com/greeting.mp3" value={data.audioUrl || ''} onChange={(e) => set('audioUrl', e.target.value)} />
        </Field>
      ) : (
        <>
          <Field label="Speech text message">
            <textarea rows={4} maxLength={500} placeholder="Enter greeting message to be read aloud..." value={data.message || ''} onChange={(e) => set('message', e.target.value)} />
          </Field>
          <Field label="Voice">
            <select value={data.voice || 'female'} onChange={(e) => set('voice', e.target.value)}>
              <option value="female">Female</option>
              <option value="male">Male</option>
            </select>
          </Field>
        </>
      )}
    </>
  );
}

function MenuSettings({ data, set }) {
  const options = data.options || [];
  const update = (idx, field, value) => set('options', options.map((o, i) => (i === idx ? { ...o, [field]: value } : o)));
  const add = () => {
    const used = options.map((o) => o.key);
    const key = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '0'].find((k) => !used.includes(k));
    if (key) set('options', [...options, { key, label: `Option ${key}` }]);
  };
  return (
    <>
      <Field label="Menu prompt">
        <textarea rows={3} maxLength={500} placeholder="For sales, press 1. For support, press 2." value={data.message || ''} onChange={(e) => set('message', e.target.value)} />
      </Field>
      <div className="form-group">
        <div className="flex-header">
          <label>Key press routes</label>
          <button type="button" className="btn-icon-add" onClick={add} disabled={options.length >= 10}><Plus size={14} /> Add Route</button>
        </div>
        <p className="field-help" style={{ marginBottom: '8px' }}>Each key gets its own exit. If the caller presses nothing or a wrong key, the menu repeats twice, then follows the "No key" exit.</p>
        <div className="menu-options-list">
          {options.map((o, idx) => (
            <div key={idx} className="menu-option-edit-row">
              <select className="opt-key-select" value={o.key} onChange={(e) => update(idx, 'key', e.target.value)}>
                {KEYS.map((k) => <option key={k} value={k}>{k}</option>)}
              </select>
              <input type="text" className="opt-label-input" placeholder="e.g. Call Sales" value={o.label} onChange={(e) => update(idx, 'label', e.target.value)} />
              <button type="button" className="btn-opt-delete" aria-label={`Remove key ${o.key}`} onClick={() => set('options', options.filter((_, i) => i !== idx))}><Trash2 size={14} /></button>
            </div>
          ))}
          {options.length === 0 && <div className="empty-sublist">No keys yet. Add routes to link other blocks.</div>}
        </div>
      </div>
    </>
  );
}

function DayChips({ value, onChange }) {
  const days = value || DAYS.map(([d]) => d);
  return (
    <div className="day-chips">
      {DAYS.map(([d, label]) => (
        <button type="button" key={d} className={`day-chip ${days.includes(d) ? 'on' : ''}`} aria-pressed={days.includes(d)}
          onClick={() => onChange(days.includes(d) ? days.filter((x) => x !== d) : [...days, d])}>{label}</button>
      ))}
    </div>
  );
}

function HoursSettings({ data, set }) {
  const hours = data.hours || { start: '09:00', end: '18:00' };
  return (
    <>
      <Field label="Time zone">
        <select value={data.timezone || 'Asia/Kolkata'} onChange={(e) => set('timezone', e.target.value)}>
          {[...new Set([data.timezone || 'Asia/Kolkata', ...TIMEZONES])].map((tz) => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
        </select>
      </Field>
      <Field label="Working days"><DayChips value={data.days} onChange={(v) => set('days', v)} /></Field>
      <div className="form-row-2">
        <Field label="Opening time"><input type="time" value={hours.start} onChange={(e) => set('hours', { ...hours, start: e.target.value })} /></Field>
        <Field label="Closing time"><input type="time" value={hours.end} onChange={(e) => set('hours', { ...hours, end: e.target.value })} /></Field>
      </div>
      <span className="field-help">Calls outside these days and hours follow the <strong>Closed</strong> exit; the rest follow <strong>Open</strong>.</span>
    </>
  );
}

function TeamSettings({ data, set, team }) {
  const members = data.members || [];
  const toggle = (id) => set('members', members.includes(id) ? members.filter((m) => m !== id) : [...members, id]);
  return (
    <>
      <Field label="Team name"><input type="text" maxLength={80} placeholder="Sales" value={data.queueName || ''} onChange={(e) => set('queueName', e.target.value)} /></Field>
      <Field label="Ringing strategy">
        <select value={data.strategy || 'ring-all'} onChange={(e) => set('strategy', e.target.value)}>
          <option value="ring-all">Ring all at once</option>
          <option value="linear">One by one, in this order</option>
          <option value="round-robin">Round robin: start with the next person each call</option>
        </select>
      </Field>
      <Field label={data.strategy === 'ring-all' || !data.strategy ? 'Ring for (seconds)' : 'Ring each person for (seconds)'}>
        <input type="number" min={5} max={120} value={data.ringTimeout || 20} onChange={(e) => set('ringTimeout', Math.max(5, Math.min(120, parseInt(e.target.value, 10) || 20)))} />
      </Field>
      <Field label={`Team members to ring (${members.length})`} help="Members ring on the mobile number in their profile.">
        <div className="member-list">
          {team.map((t) => (
            <label key={t.user_id} className={`member-check ${t.phone ? '' : 'disabled'}`}>
              <input type="checkbox" checked={members.includes(t.user_id)} disabled={!t.phone && !members.includes(t.user_id)} onChange={() => toggle(t.user_id)} />
              <span>{t.name}<small>{t.phone ? prettyNumber(t.phone) : 'No mobile number'}</small></span>
            </label>
          ))}
          {team.length === 0 && <div className="empty-sublist">No team members found.</div>}
        </div>
      </Field>
    </>
  );
}

function ForwardSettings({ data, set }) {
  return (
    <>
      <Field label="Forward to number"><input type="tel" placeholder="+91 98765 43210" value={data.forwardNumber || ''} onChange={(e) => set('forwardNumber', e.target.value)} /></Field>
      <Field label="Ring for (seconds)"><input type="number" min={5} max={120} value={data.ringTimeout || 20} onChange={(e) => set('ringTimeout', Math.max(5, Math.min(120, parseInt(e.target.value, 10) || 20)))} /></Field>
      <Field label="Announcement before transferring">
        <textarea rows={3} maxLength={500} value={data.announcement || ''} onChange={(e) => set('announcement', e.target.value)} placeholder="Message spoken before transferring..." />
      </Field>
    </>
  );
}

function VoicemailSettings({ data, set }) {
  return (
    <>
      <Field label="Message before the beep">
        <textarea rows={3} maxLength={500} placeholder="Please leave a message after the beep." value={data.message || ''} onChange={(e) => set('message', e.target.value)} />
      </Field>
      <Field label="Email the recording to (optional)" help="Voicemails are always saved in Calls & Conversations too.">
        <input type="email" placeholder="voicemail@yourcompany.com" value={data.emailDelivery || ''} onChange={(e) => set('emailDelivery', e.target.value)} />
      </Field>
    </>
  );
}

function CalendarSettings({ data, set }) {
  const holidays = data.holidays || [];
  const [day, setDay] = useState('');
  const add = (e) => {
    e.preventDefault();
    if (day && !holidays.includes(day)) set('holidays', [...holidays, day].sort());
    setDay('');
  };
  return (
    <>
      <Field label="Calendar name"><input type="text" maxLength={80} placeholder="National Holidays 2026" value={data.holidayListName || ''} onChange={(e) => set('holidayListName', e.target.value)} /></Field>
      <Field label="Time zone">
        <select value={data.timezone || 'Asia/Kolkata'} onChange={(e) => set('timezone', e.target.value)}>
          {[...new Set([data.timezone || 'Asia/Kolkata', ...TIMEZONES])].map((tz) => <option key={tz} value={tz}>{tz.replace('_', ' ')}</option>)}
        </select>
      </Field>
      <Field label={`Holiday dates (${holidays.length})`}>
        <div className="agent-add-row" style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
          <input type="date" value={day} onChange={(e) => setDay(e.target.value)} />
          <button type="button" className="btn-add-agent-sub" onClick={add}>Add</button>
        </div>
        <div className="agents-chip-list">
          {holidays.map((h) => (
            <div key={h} className="agent-chip" style={{ backgroundColor: 'rgba(168, 85, 247, 0.15)', borderColor: 'rgba(168, 85, 247, 0.3)' }}>
              <span>{h}</span><button type="button" aria-label={`Remove ${h}`} onClick={() => set('holidays', holidays.filter((x) => x !== h))}><X size={12} /></button>
            </div>
          ))}
        </div>
      </Field>
    </>
  );
}

function HangUpSettings({ data, set }) {
  return (
    <Field label="How the call ends">
      <select value={data.reason || 'completed'} onChange={(e) => set('reason', e.target.value)}>
        <option value="completed">Normal hang-up</option>
        <option value="busy">Busy signal</option>
        <option value="rejected">Reject the call</option>
      </select>
    </Field>
  );
}

function SmsSettings({ data, set }) {
  return (
    <Field label="SMS text" help="Greeting SMS is coming soon. A flow with this block can be saved but not published yet.">
      <textarea rows={5} value={data.smsText || ''} onChange={(e) => set('smsText', e.target.value)} />
    </Field>
  );
}

const SETTINGS = {
  start: StartSettings, playAudio: PlayAudioSettings, ivrMenu: MenuSettings, officeHours: HoursSettings, team: TeamSettings,
  callForward: ForwardSettings, voicemail: VoicemailSettings, calendar: CalendarSettings, hangUp: HangUpSettings, sendSMS: SmsSettings,
};

export default function Inspector({ node, onChange, onDelete, onClose, readOnly, numbers, team }) {
  const [label, setLabel] = useState('');
  useEffect(() => { if (node) setLabel(node.data.label || ''); }, [node?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!node) {
    return (
      <div className="inspector-panel empty-inspector">
        <div className="empty-state-content">
          <Settings size={40} className="rotating-icon" />
          <h4>Properties Panel</h4>
          <p>Click any block on the canvas to see its settings{readOnly ? '' : ' and change them'}.</p>
        </div>
      </div>
    );
  }

  const { id, type, data } = node;
  const set = (field, value) => onChange(id, { ...data, [field]: value });
  const Settings2 = SETTINGS[type];
  const [Icon, color] = ICONS[type] || [Settings, undefined];

  return (
    <div className="inspector-panel">
      <div className="inspector-header">
        <div className="inspector-header-left">
          <Icon className="node-hdr-icon" style={{ color }} />
          <div><h4>{readOnly ? 'Block Settings' : 'Edit Block Settings'}</h4><span className="block-type-badge">{type.toUpperCase()} BLOCK</span></div>
        </div>
        <button type="button" className="btn-close-inspector" aria-label="Close settings" onClick={onClose}><X size={18} /></button>
      </div>
      <fieldset className="inspector-body" disabled={readOnly} style={{ border: 0, margin: 0, minWidth: 0 }}>
        <Field label="Block title">
          <input type="text" maxLength={80} value={label} placeholder="Edit block name..."
            onChange={(e) => { setLabel(e.target.value); onChange(id, { ...data, label: e.target.value }); }} />
        </Field>
        <div className="divider-glow" />
        <div className="inspector-fields">{Settings2 ? <Settings2 data={data} set={set} numbers={numbers} team={team} /> : <p>No settings.</p>}</div>
      </fieldset>
      {!readOnly && (
        <div className="inspector-footer">
          <button type="button" className="btn-delete-node" onClick={() => { onDelete(id); onClose(); }}><Trash2 size={16} /> Delete Block</button>
        </div>
      )}
    </div>
  );
}
